import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Field,
  Input,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Label,
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import * as ComponentFunctions from './Protocol.ExportVisit.Form.Functions';
import { IConsumptionsFilters } from '../../../models/Filters/ConsumptionsFilters';
import { DateTimeInput } from '../../../commons/component/Inputs/DateTimeInput/DateTimeInput';
import './Protocol.ExportVisit.Form.Styles.css';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProtocolExportVisitFormComponentCode = (props: ComponentFunctions.Props & DrawerProps) => {
  const [t] = useTranslation();
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [filterText, setFilterText] = useState<string>('');

  // Descarga automática cuando el blob esté disponible y cierra el drawer
  useEffect(() => {
    if (props.FileBlob) {
      downloadFile(props.FileBlob);
      // Cerrar el drawer después de iniciar la descarga
      setTimeout(() => {
        props.onClose();
      }, 500);
    }
  }, [props.FileBlob]);

  const downloadFile = (blob: Blob) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ProtocolVisits_${new Date().getTime()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    const filters: IConsumptionsFilters = {
      SelectedConsumptions: [],
      SelectedProtocolPatients: [],
      SelectedContractConcepts: [],
      SelectedCurrencys: '',
      SelectedContractStages: [],
      SelectedPractitioners: [],
      SelectedStatus: [],
      SelectedProtocols: [],
      SelectedSettlements: [],
      SelectedDateRange: [dateFrom, dateTo],
      FilterText: filterText || undefined,
      ignoreVisitConsumption: false,
      showGroupedByVisit: false,
      GroupId: undefined
    };

    props.ExportProtocolVisitsToExcel(filters);
  };

  const isFormValid = () => {
    return dateFrom && dateTo && dateTo >= dateFrom;
  };

  return (
    <OverlayDrawer open={props.isOpen} position="end" size="small">
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={props.onClose}
            />
          }
        >
          <Label size="large" weight="semibold">
            {t('protocolExportVisitForm.title')}
          </Label>
        </DrawerHeaderTitle>
      </DrawerHeader>
      
      <DrawerBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {props.error && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>{t('protocolExportVisitForm.error')}</MessageBarTitle>
                {props.error.ErrorMessage}
              </MessageBarBody>
            </MessageBar>
          )}

          <div style={{ display: 'flex', gap: '15px', flexDirection: 'column' }}>
            
              <DateTimeInput
                label={t('protocolExportVisitForm.dateFrom')}
                placeHolder={t('protocolExportVisitForm.selectStartDate')}
                selectedDate={dateFrom}
                isSubmitted={false}
                isRequired={true}
                errorMessage={undefined}
                onSelectDate={setDateFrom}
              />
           
              <DateTimeInput
                label={t('protocolExportVisitForm.dateTo')}
                placeHolder={t('protocolExportVisitForm.selectEndDate')}
                selectedDate={dateTo}
                isSubmitted={false}
                isRequired={true}
                errorMessage={undefined}
                onSelectDate={setDateTo}
              />
            </div>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          <Button appearance="secondary" onClick={props.onClose}>
            {t('protocolExportVisitForm.cancel')}
          </Button>
          <Button
            appearance="primary"
            onClick={handleExport}
            disabled={!isFormValid() || props.isGenerating}
          >
            {t('protocolExportVisitForm.exportToExcel')}
          </Button>
          {props.isGenerating && (
            <Spinner label={t('protocolExportVisitForm.generatingFile')} />
          )}
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ProtocolExportVisitFormComponentCode as any);

